import { apiClient } from "./client";

export interface ChatStateResponse {
  conversations: unknown[];
  messages: Record<string, unknown[]>;
}

export interface ListChatConversationsResponse {
  items: unknown[];
}

export interface ListChatMessagesResponse {
  items: unknown[];
  serverTime?: number;
  /** Opaque cursor for the next (older) page, or null when fully drained. */
  nextCursor?: string | null;
  hasMore?: boolean;
}

export interface ListChatMessagesOptions {
  /** Cursor returned from a previous call. Omit for the first page. */
  before?: string;
  /** Max items to return. Server clamps to 1..200, default 50. */
  limit?: number;
}

export interface LinkPreviewResponse {
  url: string;
  title: string;
  description?: string;
  image?: string;
  siteName?: string;
}

export async function getChatState(): Promise<ChatStateResponse> {
  return apiClient.get<ChatStateResponse>("/api/chat/state");
}

export async function saveChatState(state: ChatStateResponse): Promise<void> {
  await apiClient.put("/api/chat/state", state);
}

export async function listChatConversations(): Promise<ListChatConversationsResponse> {
  return apiClient.get<ListChatConversationsResponse>("/api/chat/conversations");
}

export async function createChatConversation(
  conversation: Record<string, unknown>
): Promise<unknown> {
  return apiClient.post("/api/chat/conversations", conversation);
}

export async function updateChatConversation(
  id: string,
  updates: Record<string, unknown>
): Promise<unknown> {
  return apiClient.put(`/api/chat/conversations/${id}`, updates);
}

export interface ChatConversationStateUpdate {
  /** Stamp lastReadAt = now on the server — marks the conversation read. */
  markRead?: boolean;
  isPinned?: boolean;
  isMuted?: boolean;
  isArchived?: boolean;
}

/**
 * Per-user conversation state (read marker + pin/mute/archive). Unlike
 * updateChatConversation this writes the caller's OWN state row — pinning
 * or muting here never leaks to the other participants, and markRead
 * drives the server-computed unread counts in listChatConversations.
 */
export async function updateChatConversationState(
  id: string,
  updates: ChatConversationStateUpdate
): Promise<void> {
  await apiClient.post(`/api/chat/conversations/${id}/state`, updates);
}

export async function deleteChatConversation(id: string): Promise<void> {
  await apiClient.delete(`/api/chat/conversations/${id}`);
}

export async function listChatMessages(
  conversationId: string,
  options: ListChatMessagesOptions = {}
): Promise<ListChatMessagesResponse> {
  const params = new URLSearchParams();
  if (options.before) params.set("before", options.before);
  if (typeof options.limit === "number") params.set("limit", String(options.limit));
  const qs = params.toString();
  const path = `/api/chat/conversations/${conversationId}/messages${qs ? `?${qs}` : ""}`;
  return apiClient.get<ListChatMessagesResponse>(path);
}

export async function createChatMessage(
  conversationId: string,
  message: Record<string, unknown>
): Promise<unknown> {
  return apiClient.post(
    `/api/chat/conversations/${conversationId}/messages`,
    message
  );
}

export async function updateChatMessage(
  conversationId: string,
  messageId: string,
  updates: Record<string, unknown>
): Promise<unknown> {
  return apiClient.put(
    `/api/chat/conversations/${conversationId}/messages/${messageId}`,
    updates
  );
}

export async function deleteChatMessage(
  conversationId: string,
  messageId: string
): Promise<void> {
  await apiClient.delete(
    `/api/chat/conversations/${conversationId}/messages/${messageId}`
  );
}

/**
 * Publish a typing indicator. The default impl just no-ops because the
 * server route is now a stub — typing must be published direct-from-
 * browser via the scoped Momento token. The web app registers the real
 * publisher at startup via `setChatTypingPublisher`. Other packages /
 * tests can either register their own or accept the no-op fallback.
 */
type ChatTypingPublisher = (conversationId: string, isTyping: boolean) => Promise<void>;
let chatTypingPublisher: ChatTypingPublisher = async () => {
  // no-op fallback; the server endpoint is deprecated
};

export function setChatTypingPublisher(fn: ChatTypingPublisher) {
  chatTypingPublisher = fn;
}

export async function sendChatTyping(
  conversationId: string,
  isTyping: boolean
): Promise<void> {
  await chatTypingPublisher(conversationId, isTyping);
}

export async function getChatLinkPreview(
  url: string
): Promise<LinkPreviewResponse> {
  return apiClient.post<LinkPreviewResponse>("/api/chat/link-preview", { url });
}
